// @flow
/* eslint-disable */
import { merge } from 'lodash';
import moment from 'moment';
import $ from 'jquery';

import JQueryView, { on } from './JQueryView';

type Range = {
  selector: string,
  format: string
};

type Picker = {
  format: {
    start: ?string,
    end?: string,
    template: Function
  },
  timeDetector?: Function
};

type OptionType = {
  startRange?: Range,
  endRange?: Range,
  picker: Picker,
};

const DEFAULT_CONFIG = {
  autoUpdateInput: false,
  timePicker: true,
  timePickerIncrement: 5,
  parentEl: 'body',
  locale: {
    cancelLabel: 'キャンセル',
    applyLabel: 'OK',
    daysOfWeek: ['日', '月', '火', '水', '木', '金', '土'],
    monthNames: [
      '1月',
      '2月',
      '3月',
      '4月',
      '5月',
      '6月',
      '7月',
      '8月',
      '9月',
      '10月',
      '11月',
      '12月'
    ],
    yearLabel: '年'
  }
};

const DEFAULT_OPTIONS: OptionType = {
  picker: {
    format: {
      start: 'Y/M/DD　HH:mm',
      end: 'Y/M/DD　HH:mm',
      template: (startDate, endDate) => `${startDate} - ${endDate}`
    },
    timeDetector: (dirtyTimeStr: string) => {
      const [startDateStr, endTime] = dirtyTimeStr.split('~ ');
      const startDate = moment(startDateStr.trim(), 'Y/M/DD　HH:mm');
      const endDate = moment(`${startDateStr} ${endTime}`, 'Y/M/DD HH:mm');

      if (startDate.isValid() && endDate.isValid())
        return { startDate, endDate };
      return { startDate: false, endDate: false };
    }
  },
  startRange: {
    selector: '.start-range',
    format: 'Y/M/DD HH:mm'
  },
  endRange: {
    selector: '.end-range',
    format: 'Y/M/DD HH:mm'
  }
};

export default class DateRangePicker extends JQueryView {
  startDate: moment.Moment;
  endDate: moment.Moment;
  config: Object;
  options: OptionType;
  timeDetector: Function;
  $daterangepicker: Object;
  $startRange: Object;
  $endRange: Object;

  constructor(
    wrapperSelector: string | Object,
    config: Object = {},
    options: OptionType = {}
  ) {
    super(wrapperSelector);
    this.options = merge({}, DEFAULT_OPTIONS, options);
    this.config = merge({}, DEFAULT_CONFIG, config);
    this.timeDetector = this.options.picker.timeDetector;
    const startDate = moment(
      this.$start && this.$start.val(),
      this.options.startRange && this.options.startRange.format
    );
    const endDate = moment(
      this.$end && this.$end.val(),
      this.options.endRange && this.options.endRange.format
    );
    if (startDate.isValid()) this.config.startDate = startDate;
    if (endDate.isValid()) this.config.endDate = endDate;
    if (this.config.singleDatePicker) {
      this.config.autoApply = true;
      /*
        minDate/maxDateが指定されていない場合にminYear(デフォルトは100年前)かmaxYear(デフォルトは100年後)の年を選択した際、
        月が選択できない(disabledになっている)ため、選択できるようにするためにminDate/maxDateの初期値を設定している。
      */
      if (!this.config.minDate) this.config.minDate = moment().year(this.config.minYear || moment().year() - 100).startOf('year');
      if (!this.config.maxDate) this.config.maxDate = moment().year(this.config.maxYear || moment().year() + 100).endOf('year').startOf('day');
    }
  }

  get $picker() {
    if (!this.$daterangepicker)
      this.$daterangepicker = this.$el.find('.daterange-picker');
    return this.$daterangepicker;
  }

  get $start() {
    if (!this.$startRange)
      this.$startRange = this.$el.find(this.options.startRange && this.options.startRange.selector);
    return this.$startRange;
  }

  get $end() {
    if (!this.$endRange)
      this.$endRange = this.$el.find(this.options.endRange && this.options.endRange.selector);
    return this.$endRange;
  }

  updateStartRange = (startDate: moment.Moment, picker: ?DateRangePicker) => {
    const newStartDate = startDate || (picker && picker.startDate);
    if (!newStartDate) return;
    this.startDate = newStartDate;
    this.$start.val(newStartDate.format(this.options.startRange && this.options.startRange.format));
    this.$start.trigger('change');
  };

  updateEndRange = (endDate: moment.Moment, picker: ?DateRangePicker) => {
    const newEndDate = endDate || (picker && picker.endDate);
    if (!newEndDate) return;
    this.endDate = newEndDate;
    this.$end.val(newEndDate.format(this.options.endRange && this.options.endRange.format));
    this.$end.trigger('change');
  };

  updatePicker = (startDate: moment.Moment, endDate: moment.Moment, picker: DateRangePicker, event: Event) => {
    $(event.currentTarget).val(this.formatPicker(startDate, endDate));
    this.$picker.trigger('change');
  };

  formatPicker = (startDate: moment.Moment, endDate: moment.Moment) => {
    const { start, end, template } = this.options.picker.format;
    return template(startDate.format(start), endDate.format(end));
  };

  applyDaterangepicker = (event: Event, picker: DateRangePicker) => {
    const { startDate, endDate } = picker;
    this.updatePicker(startDate, endDate, picker, event);
    this.updateStartRange(startDate, picker);
    this.updateEndRange(endDate, picker);
    if (this.$picker.closest('form').data('validator')) {
      this.validateInput(this.$picker);
      this.validateInput(this.$end);
      this.validateInput(this.$start);
    }
  };

  validateInput = ($input: Object) => {
    $input.length === 1 && $input.valid();
  };

  cancelDaterangepicker = (event: Event, picker: ?DateRangePicker) => {
    $(picker).val('');
    this.$start.val('');
    this.$end.val('');
    this.startDate = null;
    this.endDate = null;
  };

  @on({ event: 'change', selector: '.daterange-picker' })
  handleChangePickerValue(event: any) {
    const { timeDetector } = this;
    this.$picker.each((_, elementPicker) => {
      if (!elementPicker.value.length) {
        this.cancelDaterangepicker(event, elementPicker);
        return;
      }
      const { startDate, endDate } = timeDetector(elementPicker.value);
      if (startDate && endDate) {
        this.updateStartRange(startDate, null);
        this.updateEndRange(endDate, null);
      } else {
        $(elementPicker).val('');
      }
    });
  }

  @on({ event: 'showCalendar.daterangepicker', selector: '.daterange-picker' })
  handleChangePickerPosition(event: any, picker: any) {
    $(event.currentTarget).attr('autocomplete', 'off');
    const bodyHeight = $('body').height();
    const offset = $(event.currentTarget).offset();
    if (offset.top + 300 > bodyHeight) {
      picker.drops = 'up';
    } else {
      picker.drops = 'down';
    }
  }

  @on({ event: 'hide.daterangepicker', selector: '.daterange-picker' })
  handleHideDateRangePicker(event: Event) {
    $(event.currentTarget).attr('autocomplete', 'on');
  }

  render = () => {
    this.updateStartRange(this.config.startDate, null);
    this.updateEndRange(this.config.endDate, null);
    this.$picker.each((_, picker) => {
      $(picker).daterangepicker(this.config);
      $(picker).on('apply.daterangepicker', this.applyDaterangepicker);
    });
  };
}
